import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ForumIcon from "@mui/icons-material/Forum";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import AdesivoDialog from "../../components/AdesivoDialog";
import CartelaDialog from "../../components/CartelaDialog";

const STATUS = {
  rascunho: { label: "Rascunho", color: "#607d8b" },
  gerado: { label: "Gerado", color: "#1976d2" },
  enviado: { label: "Aguardando aprovação", color: "#ed6c02" },
  aprovado: { label: "Aprovado", color: "#2e7d32" },
};

const Adesivos = () => {
  const navigate = useNavigate();
  const [aba, setAba] = useState("pedidos"); // "pedidos" | "modelos"
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usandoTemplate, setUsandoTemplate] = useState(null);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cartelaOpen, setCartelaOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [matLabel, setMatLabel] = useState({});
  const [enviando, setEnviando] = useState(null);

  useEffect(() => {
    api.get("/adesivos/opcoes")
      .then(({ data }) => setMatLabel(Object.fromEntries((data.materiais || []).map((m) => [m.value, m.label]))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/adesivos", { params: { search } });
      setRows(data.rows || []);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, [search]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/adesivos/templates");
      setTemplates(data.rows || []);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (aba !== "pedidos") return;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load, aba]);

  useEffect(() => {
    if (aba === "modelos") loadTemplates();
  }, [aba, loadTemplates]);

  const openNew = () => { setEditingId(null); setDialogOpen(true); };
  const openNewCartela = () => { setEditingId(null); setCartelaOpen(true); };
  // Abre o diálogo certo conforme o tipo do registro (cartela x simples).
  const openEdit = (r) => {
    setEditingId(r.id);
    if (r.forma === "cartela") setCartelaOpen(true); else setDialogOpen(true);
  };

  // Cria um pedido novo a partir de um modelo (clona a config) e abre o
  // diálogo de edição já carregado com o resultado — mesmo fluxo de "editar
  // um adesivo existente" que o AdesivoDialog já suporta.
  const usarTemplate = async (tpl) => {
    setUsandoTemplate(tpl.id);
    try {
      const { data } = await api.post(`/adesivos/de-template/${tpl.id}`);
      toast.success(`Pedido criado a partir do modelo "${tpl.templateNome}".`);
      setEditingId(data.id);
      if (data.forma === "cartela") setCartelaOpen(true); else setDialogOpen(true);
    } catch (err) { toastError(err); }
    finally { setUsandoTemplate(null); }
  };

  const baixar = async (r, tipo) => {
    try {
      const { data } = await api.get(`/adesivos/${r.id}/${tipo}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${r.numero || "adesivos"}.${tipo === "pdf" ? "pdf" : "plt"}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) { toastError(err); }
  };

  // Envia a prova (imagem com marca d'água) ao atendimento vinculado, direto da lista.
  const enviarProva = async (r) => {
    if (!r.ticketId) { toast.warn("Adesivo sem atendimento vinculado. Abra o adesivo e vincule um ticket primeiro."); return; }
    setEnviando(r.id);
    try {
      await api.post(`/adesivos/${r.id}/send/${r.ticketId}`);
      toast.success("Prova enviada ao cliente.");
      load();
    } catch (err) { toastError(err); }
    finally { setEnviando(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este adesivo?")) return;
    try { await api.delete(`/adesivos/${id}`); toast.success("Excluído."); load(); }
    catch (err) { toastError(err); }
  };

  const brl = (v) => (v != null ? `R$ ${Number(v).toFixed(2).replace(".", ",")}` : "—");

  return (
    <Paper style={{ margin: 16, padding: 16 }} variant="outlined">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <ContentCutIcon color="primary" />
        <Typography variant="h6" style={{ flex: 1 }}>Adesivos</Typography>
        <ToggleButtonGroup size="small" value={aba} exclusive
          onChange={(_, v) => { if (v) setAba(v); }}>
          <ToggleButton value="pedidos"><PlaylistAddCheckIcon fontSize="small" sx={{ mr: 0.5 }} />Meus adesivos</ToggleButton>
          <ToggleButton value="modelos"><BookmarkIcon fontSize="small" sx={{ mr: 0.5 }} />Modelos</ToggleButton>
        </ToggleButtonGroup>
        {aba === "pedidos" && (
          <TextField size="small" placeholder="Buscar nº / descrição"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }} />
        )}
        <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewCartela}>Nova cartela</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Novo</Button>
      </div>

      {aba === "modelos" ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Modelo</TableCell>
              <TableCell>Forma</TableCell>
              <TableCell align="center">Valor</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={22} /></TableCell></TableRow>
            )}
            {!loading && templates.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">
                <Typography variant="body2" color="textSecondary">
                  Nenhum modelo salvo ainda. Abra um adesivo e use "Salvar como modelo".
                </Typography>
              </TableCell></TableRow>
            )}
            {templates.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell>
                  {t.templateNome}
                  <Typography variant="caption" display="block" color="textSecondary">{t.numero}</Typography>
                </TableCell>
                <TableCell>
                  {t.forma}
                  <Typography variant="caption" display="block" color="textSecondary">
                    {t.medida}{t.config?.material && matLabel[t.config.material] ? ` · ${matLabel[t.config.material]}` : ""}
                  </Typography>
                </TableCell>
                <TableCell align="center">{brl(t.precoFolha)}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="contained" startIcon={usandoTemplate === t.id ? <CircularProgress size={14} /> : <AddIcon />}
                    disabled={usandoTemplate === t.id}
                    onClick={() => usarTemplate(t)}>
                    Usar modelo
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nº</TableCell>
            <TableCell>Descrição</TableCell>
            <TableCell>Forma</TableCell>
            <TableCell>Atendimento</TableCell>
            <TableCell align="center">Folhas</TableCell>
            <TableCell align="center">Valor</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell>Impresso por</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow><TableCell colSpan={9} align="center"><CircularProgress size={22} /></TableCell></TableRow>
          )}
          {!loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={9} align="center"><Typography variant="body2" color="textSecondary">Nenhum adesivo.</Typography></TableCell></TableRow>
          )}
          {rows.map((r) => {
            const st = STATUS[r.status] || STATUS.rascunho;
            return (
              <TableRow key={r.id} hover>
                <TableCell>{r.numero}</TableCell>
                <TableCell>{r.descricao || "—"}</TableCell>
                <TableCell>
                  {r.forma}
                  <Typography variant="caption" display="block" color="textSecondary">
                    {r.medida}{r.config?.material && matLabel[r.config.material] ? ` · ${matLabel[r.config.material]}` : ""}
                  </Typography>
                </TableCell>
                <TableCell>
                  {r.ticketId ? (
                    <Tooltip title="Ir ao atendimento vinculado">
                      <span onClick={() => navigate(`/tickets/${r.ticketId}`)}
                        style={{ cursor: "pointer", color: "#00897b", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <ForumIcon fontSize="small" />
                        {r.contact?.name || `#${r.ticketId}`}
                      </span>
                    </Tooltip>
                  ) : <Typography variant="caption" color="textSecondary">—</Typography>}
                </TableCell>
                <TableCell align="center">
                  {r.folhas || "—"}
                  {r.totalAdesivos > 0 && (
                    <Typography variant="caption" display="block" color="textSecondary">{r.totalAdesivos} un</Typography>
                  )}
                </TableCell>
                <TableCell align="center">{brl(r.valorTotal)}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={st.label} style={{ backgroundColor: st.color, color: "#fff" }} />
                </TableCell>
                <TableCell>
                  {r.impressoPor?.name ? (
                    <Tooltip title={r.impressoEm ? new Date(r.impressoEm).toLocaleString("pt-BR") : ""}>
                      <Typography variant="caption">{r.impressoPor.name}</Typography>
                    </Tooltip>
                  ) : <Typography variant="caption" color="textSecondary">—</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar / gerar"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title={r.arquivoPdfFileName ? "Baixar PDF de impressão" : "Gere o PDF primeiro"}>
                    <span>
                      <IconButton size="small" disabled={!r.arquivoPdfFileName} onClick={() => baixar(r, "pdf")}>
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={r.arquivoPltFileName ? "Baixar arquivo de corte (.plt)" : "Gere o corte primeiro"}>
                    <span>
                      <IconButton size="small" disabled={!r.arquivoPltFileName} onClick={() => baixar(r, "plt")}>
                        <ContentCutIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={!r.ticketId ? "Vincule um atendimento primeiro (abra o adesivo)" : (!r.arquivoPdfFileName ? "Gere o PDF primeiro" : "Enviar prova ao cliente")}>
                    <span>
                      <IconButton size="small" disabled={!r.ticketId || !r.arquivoPdfFileName || enviando === r.id} onClick={() => enviarProva(r)}>
                        {enviando === r.id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {r.arteAprovada && (
                    <Tooltip title="Arte aprovada pelo cliente">
                      <CheckCircleIcon fontSize="small" style={{ color: "#2e7d32", verticalAlign: "middle" }} />
                    </Tooltip>
                  )}
                  <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      )}

      <AdesivoDialog
        open={dialogOpen}
        adesivoId={editingId}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { load(); if (aba === "modelos") loadTemplates(); }}
      />
      <CartelaDialog
        open={cartelaOpen}
        adesivoId={editingId}
        onClose={() => setCartelaOpen(false)}
        onSaved={() => { load(); if (aba === "modelos") loadTemplates(); }}
      />
    </Paper>
  );
};

export default Adesivos;
